import { sectionRenderer } from '@theme/section-renderer';
import { FilterUpdateEvent } from '@theme/events';

/**
 * <quick-filter-pills section-id="...">
 *
 * A capsule-pill carousel that toggles a single facet param in the URL and
 * re-renders the whole collection section through the theme's render/morph
 * engine — WITHOUT a hard reload — staying in harmony with the native facets
 * form (checkboxes, sort, clear-all).
 *
 * Harmony is by query-string composition only: a click edits ONLY the pill's
 * own `name=value` pair; every other filter.* / sort_by / q / tag-or rides
 * along untouched. Active state is recomputed server-side each render, so no
 * client flag has to survive the morph.
 *
 * The click listener is delegated on the host element. On morph the host is
 * patched in place (matched by tag), so connectedCallback does not re-run and
 * the listener is never double-bound; if the host is ever replaced instead,
 * the fresh node's connectedCallback re-binds and the old node's
 * disconnectedCallback cleans up. Either way: exactly one handler.
 */
class QuickFilterPills extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    // After every facets render (pills, checkboxes, sort, clear): reconcile pill
    // state from the URL, tear down the loading skeleton, and re-hydrate images.
    document.addEventListener('facets:rendered', this.#handleRendered);
    window.addEventListener('popstate', this.#syncActiveStates);
    // Reflect the current URL immediately on load (authoritative over server guess).
    this.#syncActiveStates();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener('facets:rendered', this.#handleRendered);
    window.removeEventListener('popstate', this.#syncActiveStates);
  }

  /**
   * Runs after the section morphs in fresh product HTML (fires for pills,
   * checkboxes, sort, clear-all, and the self-owned fallback path alike):
   *   1. Reconcile every pill's active state from the authoritative URL.
   *   2. Remove the skeleton overlay (morph usually drops it already; this is
   *      the safety net so it can never get stuck).
   *   3. Re-hydrate images so DOMParser-inserted <img> nodes actually paint.
   */
  #handleRendered = () => {
    this.#syncActiveStates();
    this.#removeSkeleton();
    this.#hydrateImages();
  };

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('quick-filter-pills: section-id attribute is required');
    return id;
  }

  /**
   * Compute each pill's active state from the ACTUAL URL — the single source of truth
   * for what's currently filtered. This is robust whether or not the facet is a
   * configured storefront filter, and regardless of how collection.filters resolves
   * inside the theme block. Drives the class, aria-pressed, and the dismiss affordance.
   */
  #syncActiveStates = () => {
    const params = new URL(window.location.href).searchParams;
    for (const pill of this.querySelectorAll('.qfp-pill')) {
      const name = pill.dataset.filterName;
      const value = pill.dataset.filterValue;
      const active = Boolean(name) && Boolean(value) && params.getAll(name).includes(value);
      pill.classList.toggle('qfp-pill--active', active);
      pill.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  };

  #handleClick = (event) => {
    const pill = event.target.closest('.qfp-pill');
    if (!pill || !this.contains(pill)) return;
    event.preventDefault();

    const name = pill.dataset.filterName;
    const value = pill.dataset.filterValue;
    if (!name || !value) return;

    // URL-authoritative (not the rendered attribute), so toggle-off is always correct.
    const isActive = new URL(window.location.href).searchParams.getAll(name).includes(value);
    const removeUrl = pill.dataset.removeUrl; // server-computed exact removal URL (when available)

    let targetUrl;
    if (isActive && removeUrl) {
      // Most reliable toggle-off: Shopify's own url_to_remove preserves every other param.
      targetUrl = new URL(removeUrl, window.location.href);
    } else {
      targetUrl = this.#toggleParam(new URL(window.location.href), name, value);
    }

    // Mirror facets.js createURLParameters: a filter change resets pagination.
    targetUrl.searchParams.delete('page');

    // ── Instant perceived performance (do NOT wait for the AJAX round-trip) ──
    // 1) Flip the clicked pill's visual state immediately so the tap feels live.
    // 2) Drop a skeleton grid into the results wrapper BEFORE the fetch starts.
    // Both are reconciled/overwritten authoritatively once the section morphs
    // (#handleRendered re-syncs the pill from the URL; morph replaces the grid).
    this.#setPillActive(pill, !isActive);
    this.#showSkeleton();

    this.#applyAndRender(targetUrl);
  };

  /**
   * Optimistically set a single pill's active state without waiting for the
   * render. The post-morph #syncActiveStates() is still the source of truth, so
   * a wrong guess (rare) self-corrects within the same interaction.
   * @param {HTMLElement} pill
   * @param {boolean} active
   */
  #setPillActive(pill, active) {
    pill.classList.toggle('qfp-pill--active', active);
    pill.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  /**
   * Toggle exactly one name=value pair against the live URL, preserving all
   * other params. Multi-value safe: only the matching value is removed; other
   * values under the same facet name survive. Decided by the actual URL (not a
   * server flag), so it is correct even for facets that aren't configured as
   * storefront filters.
   * @param {URL} url
   * @param {string} name
   * @param {string} value
   * @returns {URL}
   */
  #toggleParam(url, name, value) {
    const existing = url.searchParams.getAll(name);
    if (existing.includes(value)) {
      url.searchParams.delete(name);
      for (const v of existing) {
        if (v !== value) url.searchParams.append(name, v);
      }
    } else {
      url.searchParams.append(name, value);
    }
    return url;
  }

  /**
   * Prefer the facets engine's own public hook so clear-all + sort stay in sync
   * (it dispatches FilterUpdateEvent and renders via sectionRenderer, morphing
   * the whole shopify-section wrapper — grid + pills together). Fall back to a
   * self-owned pushState + render when no facets form exists for this section.
   * @param {URL} targetUrl
   */
  #applyAndRender(targetUrl) {
    const facetsForm = this.#findFacetsForm();

    if (facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
      try {
        // facets.js: history.pushState(url) + dispatch FilterUpdateEvent + #updateSection()
        // (#updateSection -> sectionRenderer.renderSection(sectionId) reads the pushed URL,
        //  morphs #shopify-section-<id>, then dispatches document 'facets:rendered').
        facetsForm.updateFiltersByURL(targetUrl.toString());

        // facets.js builds its FilterUpdateEvent from the form's checkboxes, which do NOT
        // include a pill-only filter, so "Clear all" can briefly read inactive until the
        // morph self-corrects. Re-dispatch with the toggled URL's params (idempotent: the
        // clear/remove components, which listen on document, only recompute a CSS class).
        document.dispatchEvent(new FilterUpdateEvent(targetUrl.searchParams));
        return;
      } catch (error) {
        // fall through to the self-contained path
      }
    }

    // Fallback: no facets form on this collection — own the full cycle.
    history.pushState({}, '', targetUrl.toString());
    Promise.resolve(
      // Fresh clone: buildSectionRenderingURL mutates the URL it receives.
      sectionRenderer.renderSection(this.sectionId, { url: new URL(targetUrl.toString()) })
    )
      .catch(() => {})
      .finally(() => document.dispatchEvent(new CustomEvent('facets:rendered')));
  }

  /* ─────────────────────────── Skeleton loading ─────────────────────────── */

  /** The <div id="ResultsList"> wrapper of THIS section's product grid (or null). */
  #resultsContainer() {
    const root =
      document.getElementById(`shopify-section-${this.sectionId}`) || this.closest('.shopify-section');
    return (root ?? document).querySelector('#ResultsList');
  }

  /**
   * Inject an instant skeleton grid over the results wrapper. Rendered the
   * moment a pill is clicked, BEFORE the fetch, so there is never a dead
   * no-feedback gap. morph() drops it when the real HTML arrives (and
   * #removeSkeleton() guarantees teardown either way). Card count mirrors the
   * current grid, clamped to 4–8.
   */
  #showSkeleton() {
    const container = this.#resultsContainer();
    if (!container || container.querySelector('.qfp-skeleton-overlay')) return;

    const rendered = container.querySelectorAll('.product-grid__item').length;
    const count = Math.min(8, Math.max(4, rendered || 8));

    const card =
      '<div class="qfp-skel-card">' +
      '<div class="qfp-skel-img"></div>' +
      '<div class="qfp-skel-line qfp-skel-line--title"></div>' +
      '<div class="qfp-skel-line qfp-skel-line--price"></div>' +
      '</div>';

    const overlay = document.createElement('div');
    overlay.className = 'qfp-skeleton-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML = card.repeat(count); // static markup only — no user data
    container.appendChild(overlay);
  }

  /** Remove any skeleton overlay belonging to THIS section (idempotent). */
  #removeSkeleton() {
    const root =
      document.getElementById(`shopify-section-${this.sectionId}`) || this.closest('.shopify-section');
    (root ?? document).querySelectorAll('.qfp-skeleton-overlay').forEach((el) => el.remove());
  }

  /**
   * Post-morph image hydration. morph() inserts fresh <img> nodes parsed from a
   * DOMParser document (and deliberately skips src/srcset when unchanged); some
   * of those never kick off their load and render blank — most visibly when a
   * filter is REMOVED and previously-dropped products reappear. Re-asserting
   * src/srcset re-runs the browser's image-selection + decode for any image
   * that isn't already painted. Far-below-fold lazy images are left alone so
   * native lazy-loading still defers the long tail.
   */
  #hydrateImages() {
    const container = this.#resultsContainer();
    if (!container) return;

    const grid = container.querySelector('.product-grid') ?? container;
    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;

    for (const img of grid.querySelectorAll('img')) {
      if (img.complete && img.naturalWidth > 0) continue; // already painted
      const { top } = img.getBoundingClientRect();
      if (top > viewportH * 1.5) continue; // deep below the fold — keep it lazy
      this.#repaintImage(img);
    }
  }

  /**
   * Force a single <img> to (re)load and paint.
   * @param {HTMLImageElement} img
   */
  #repaintImage(img) {
    try {
      if (img.getAttribute('loading') === 'lazy') img.loading = 'eager';
      const srcset = img.getAttribute('srcset');
      const src = img.getAttribute('src');
      if (srcset) img.setAttribute('srcset', srcset); // re-run source selection
      if (src) img.setAttribute('src', src); // re-trigger the fetch
      if (typeof img.decode === 'function') img.decode().catch(() => {});
    } catch {
      /* never let one image break the loop */
    }
  }

  /** Find the live facets-form-component bound to THIS section id. */
  #findFacetsForm() {
    const forms = document.querySelectorAll('facets-form-component[section-id]');
    for (const form of forms) {
      if (form.getAttribute('section-id') === this.sectionId) return form;
    }
    return document.querySelector('facets-form-component');
  }
}

if (!customElements.get('quick-filter-pills')) {
  customElements.define('quick-filter-pills', QuickFilterPills);
}
